/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useEffect, useState } from 'react';
import { ContainerInventoryService } from '../../../../../service/MediaContainerServices';
import MediaPrepPageHeader from '../../../../common/commonComponents/mediaPrepPageHeader';
import MediaPrepHeaderSection from '../../../../common/commonComponents/mediaPrepheaderSection';
import { useTranslation } from 'react-i18next';
import { getLotColumns } from '../partials/lotcolumn';
import ContainerInventoryTable from '../partials/containerTable';
import ContainerLotDetails from "./containerLotDetails";
import ContainerDetailsHeader from './containerDetailsHeader';
import ContainerDetailsFooter from './containerDetailsFooter';

interface ContainerLotDetailsProps {
  batchId: string;
  onBack: () => void;
  onViewUnit?: (unitId: string) => void;
}

const ContainerDetails: React.FC<ContainerLotDetailsProps> = ({ batchId, onBack, onViewUnit }) => {
    const [selectedLot, setSelectedLot] = useState<LotInfo | null>(null);

    interface BatchInfo {
      batch_no: string;
      service_limit: number;
      total_quantity: number;
      created_at: string;
    }

    interface LotInfo {
      id: string;
      lot_code: string;
      lot_serial: string;
      lot_size: string;
      created_at: string;
    }
    const [batchInfo, setBatchInfo] = useState<BatchInfo | null>(null);
    const [loading, setLoading] = useState(false);
    const [lotList, setLotList] = useState<LotInfo[]>([]);
    const { t } = useTranslation();
    

    // Fetches container batch details for the selected batch ID
    const fetchBatchDetails = async () => {
        setLoading(true);

        try {
        const res = await ContainerInventoryService.getContainerBatch(batchId);

        if (res?.status) {
            if (res.data?.length > 0) {
            setBatchInfo(res.data[0]);
            }
        }
        } catch (error) {
        console.error("Error fetching container batch details", error);
        } finally {
        setLoading(false);
        }
    };


    const fetchLotDetails = async () => {
        setLoading(true);

        try {
        const res = await ContainerInventoryService.getContainerBatchDetails(batchId);

        if (res?.status) {
            setLotList(res.data || []);
        }
        } catch (error) {
        console.error("Error fetching container lot details", error);
        } finally {
        setLoading(false);
        }
    };

    useEffect(() => {
      if (batchId) {
        fetchBatchDetails();
      }
    }, [batchId]);

    useEffect(() => {
      if (batchId) {
        fetchLotDetails();
      }
    }, [batchId]);

    if (selectedLot) {
      return (
        <ContainerLotDetails
          lot={selectedLot}
          batchId={batchId}
          onBack={() => {
            setSelectedLot(null);
            fetchLotDetails();
            fetchBatchDetails();
          }}
        />
      );
    }


  return (
    <div className="media-prep-scope min-h-screen bg-[#F8FAFC] text-slate-900 pb-20">
       <MediaPrepHeaderSection />
         <MediaPrepPageHeader pageHeader={t('containerInventory.containerBatchDetails.pageTitle')}  />
            {/* Sticky Header */}
        <ContainerDetailsHeader
          pageTitle={t("containerInventory.containerBatchDetails.pageTitle")}
          identifier={batchInfo?.batch_no || ""}
          printLabel={t("containerInventory.containerBatchDetails.printBatchLabel")}
          onBack={onBack}
        />

        {/* Premium Informative Statistics Strip */}
        <div className="sticky top-[224px] z-30 bg-[#FBFDFE] border-b border-slate-100 py-3 px-10">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex-1 flex flex-col gap-1 border-r border-slate-100 pr-8">
              <div className="flex items-center gap-2 text-slate-400">
                <span className="material-symbols-outlined text-2xl">{t('containerInventory.icons.view_list')}</span>
                <span className="text-[9px] font-black uppercase tracking-widest">{t('containerInventory.containerBatchDetails.BatchName')}</span>
              </div>
              <span className="text-xs font-black text-[#2D3E50] truncate">{batchInfo?.batch_no}</span>
            </div>

            <div className="flex-1 flex flex-col gap-1 border-r border-slate-100 px-8">
              <div className="flex items-center gap-2 text-slate-400">
                <span className="material-symbols-outlined text-2xl">{t('containerInventory.icons.update')}</span>
                <span className="text-[9px] font-black uppercase tracking-widest">{t('containerInventory.containerBatchDetails.ServiceLimit')}</span>
              </div>
              <span className="text-xs font-black text-[#2D3E50]">{batchInfo?.service_limit} Max Uses</span>
            </div>

            <div className="flex-1 flex flex-col gap-1 border-r border-slate-100 px-8">
              <div className="flex items-center gap-2 text-slate-400">
                <span className="material-symbols-outlined text-2xl">{t('containerInventory.icons.inventory_2')}</span>
                <span className="text-[9px] font-black uppercase tracking-widest">{t('containerInventory.containerBatchDetails.TotalQuantity')}</span>
              </div>
              <span className="text-xs font-black text-[#2D3E50]">{batchInfo?.total_quantity}</span>
            </div>

            <div className="flex-1 flex flex-col gap-1 pl-8">
              <div className="flex items-center gap-2 text-slate-400">
                <span className="material-symbols-outlined text-2xl">{t('containerInventory.icons.calendar_today')}</span>
                <span className="text-[9px] font-black uppercase tracking-widest">{t('containerInventory.containerBatchDetails.CreatedDate')}</span>
              </div>
              <span className="text-xs font-black text-[#2D3E50]">{batchInfo?.created_at?.split(' ')[0] || "-"}</span>
            </div>
          </div>
      </div>
      

      <div className="p-12">
           {/* Lots Table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-8 py-6 flex justify-between items-center border-b border-slate-50 bg-[#FBFDFE]">
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-orange-500 font-bold">{t('containerInventory.icons.layers')}</span>
                <h3 className="text-base font-black text-[#2D3E50] tracking-tight uppercase">{t('containerInventory.containerBatchDetails.assignedLots')}</h3>
              </div>
            </div>

            <ContainerInventoryTable
              columns={getLotColumns(t)}
              data={lotList}
              actionHeader={t("containerInventory.containerBatchDetails.Actions")}
              renderAction={(row) => (
                <button
                  onClick={() => setSelectedLot(row)}
                  className="flex items-left gap-1.5 px-6 py-2 bg-[#F1F5F9] text-slate-600 rounded-lg font-black text-[10px] uppercase tracking-widest hover:bg-[#2D3E50] hover:text-white active:scale-95 transition-all shadow-sm ml-auto border-none outline-none focus:outline-none"
                >
                  <span className="material-symbols-outlined text-[14px]">
                    {t("containerInventory.icons.visibility")}
                  </span>
                  {t("containerInventory.containerBatchDetails.ViewDetails")}
                </button>
              )}
            />
          </div>

        {/* Footer Actions */}
        <ContainerDetailsFooter onBack={onBack} />
      </div>
    </div>
  );
};

export default ContainerDetails;
